import React, { useEffect, useState } from 'react'
import { TextField, Banner, Button, IconButton, ConfirmDialog, SegmentedToggle } from '@ui'
import { useAdminSettings } from '../hooks/useAdminSettings.js'
import { useAuth } from '../../auth.jsx'

export function SettingsPage({ me }) {
  const { settings, admins, loading, updateSettings, addAdmin, removeAdmin } = useAdminSettings()
  const [newAdminEmail, setNewAdminEmail] = useState('')
  const [addingAdmin, setAddingAdmin] = useState(false)
  const [addError, setAddError] = useState(null)
  const [adminToRemove, setAdminToRemove] = useState(null)
  const [newDomain, setNewDomain] = useState('')
  const [domainError, setDomainError] = useState(null)
  const [suspensionDays, setSuspensionDays] = useState('')
  const [suspensionDaysSaved, setSuspensionDaysSaved] = useState(false)
  const [retentionDays, setRetentionDays] = useState('')
  const [retentionDaysSaved, setRetentionDaysSaved] = useState(false)
  const [graceDays, setGraceDays] = useState('')
  const [graceDaysSaved, setGraceDaysSaved] = useState(false)
  const [mfaSaving, setMfaSaving] = useState(false)
  const [mfaEnableConfirm, setMfaEnableConfirm] = useState(false)
  const { signOut } = useAuth()

  const mfaEnforced = !!settings?.mfaEnforced

  function handleSetMfa(next) {
    if (mfaSaving || next === mfaEnforced) return
    if (next) {
      setMfaEnableConfirm(true)
      return
    }
    void persistMfa(false)
  }

  async function persistMfa(next) {
    setMfaSaving(true)
    try {
      await updateSettings({ mfaEnforced: next })
    } finally {
      setMfaSaving(false)
    }
  }

  async function handleConfirmEnableMfa() {
    setMfaEnableConfirm(false)
    await persistMfa(true)
    signOut()
  }

  useEffect(() => {
    if (settings?.suspensionRetentionDays != null) {
      setSuspensionDays(String(settings.suspensionRetentionDays))
    }
  }, [settings?.suspensionRetentionDays])

  useEffect(() => {
    setRetentionDays(settings?.dataRetentionDays != null ? String(settings.dataRetentionDays) : '')
  }, [settings?.dataRetentionDays])

  useEffect(() => {
    if (settings?.deletionGraceDays != null) {
      setGraceDays(String(settings.deletionGraceDays))
    }
  }, [settings?.deletionGraceDays])

  async function handleSuspensionDaysSave(e) {
    e.preventDefault()
    const val = parseInt(suspensionDays, 10)
    if (!val || val < 1) return
    await updateSettings({ suspensionRetentionDays: val })
    setSuspensionDaysSaved(true)
    setTimeout(() => setSuspensionDaysSaved(false), 2000)
  }

  async function handleRetentionDaysSave(e) {
    e.preventDefault()
    const val = retentionDays.trim() === '' ? null : parseInt(retentionDays, 10)
    if (val !== null && (!val || val < 1)) return
    await updateSettings({ dataRetentionDays: val })
    setRetentionDaysSaved(true)
    setTimeout(() => setRetentionDaysSaved(false), 2000)
  }

  async function handleGraceDaysSave(e) {
    e.preventDefault()
    const val = parseInt(graceDays, 10)
    if (!val || val < 1) return
    await updateSettings({ deletionGraceDays: val })
    setGraceDaysSaved(true)
    setTimeout(() => setGraceDaysSaved(false), 2000)
  }

  async function handleAddAdmin(e) {
    e.preventDefault()
    setAddingAdmin(true)
    setAddError(null)
    try {
      await addAdmin(newAdminEmail)
      setNewAdminEmail('')
    } catch (err) {
      setAddError(err.message || 'Failed to add admin.')
    } finally {
      setAddingAdmin(false)
    }
  }

  const allowedDomains = settings?.allowedDomains || []

  async function handleAddDomain(e) {
    e.preventDefault()
    setDomainError(null)
    const domain = newDomain.trim().toLowerCase().replace(/^@/, '')
    if (!domain || !domain.includes('.')) {
      setDomainError('Enter a valid domain, e.g. company.com')
      return
    }
    if (allowedDomains.includes(domain)) {
      setDomainError('Domain already in the list.')
      return
    }
    try {
      await updateSettings({ allowedDomains: [...allowedDomains, domain] })
      setNewDomain('')
    } catch {
      setDomainError('Failed to save.')
    }
  }

  async function handleRemoveDomain(domain) {
    await updateSettings({ allowedDomains: allowedDomains.filter((d) => d !== domain) })
  }

  if (loading) return <div style={styles.loading}>Loading…</div>

  return (
    <div style={styles.wrap}>
      <h2 style={styles.title}>Settings</h2>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Administrators</div>
        {admins.map((a) => (
          <div key={a.userId} style={styles.adminRow}>
            <div>
              <div style={styles.adminName}>{a.name || a.email}</div>
              <div style={styles.adminEmail}>{a.email}</div>
            </div>
            {a.userId !== me?.userId && (
              <Button size="sm" variant="danger" onClick={() => setAdminToRemove(a)}>
                Remove
              </Button>
            )}
            {a.userId === me?.userId && (
              <span style={styles.you}>(you)</span>
            )}
          </div>
        ))}
        {addError && <Banner tone="error">{addError}</Banner>}
        <form onSubmit={handleAddAdmin} style={styles.addForm}>
          <div style={{ flex: 1 }}>
            <TextField
              type="email"
              placeholder="Add admin by email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              required
              aria-label="Add admin by email"
            />
          </div>
          <Button type="submit" loading={addingAdmin} loadingLabel="Adding…">
            + Add admin
          </Button>
        </form>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Sign-up</div>
        <div style={styles.domainHint}>
          {allowedDomains.length === 0
            ? 'No restriction — any email domain is accepted.'
            : 'Only these domains may be invited:'}
        </div>
        {allowedDomains.length > 0 && (
          <div style={styles.chipList}>
            {allowedDomains.map((d) => (
              <span key={d} style={styles.chip}>
                {d}
                <IconButton ariaLabel="Remove domain" onClick={() => handleRemoveDomain(d)}>×</IconButton>
              </span>
            ))}
          </div>
        )}
        {domainError && <Banner tone="error">{domainError}</Banner>}
        <form onSubmit={handleAddDomain} style={styles.addForm}>
          <div style={{ flex: 1 }}>
            <TextField
              type="text"
              placeholder="company.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              aria-label="Add allowed sign-up domain"
            />
          </div>
          <Button type="submit">+ Add domain</Button>
        </form>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Security</div>
        <div style={styles.retentionRow}>
          <div style={styles.retentionExplain}>
            <div style={styles.fieldLabel}>Require MFA for all members</div>
            <div style={styles.domainHint}>
              When enabled, members without TOTP (authenticator app) will be
              prompted to set it up immediately after sign-in, and cannot use
              the app until they do. Passkeys alone do not satisfy this
              requirement.
            </div>
          </div>
          <div style={styles.retentionControl}>
            <SegmentedToggle
              ariaLabel="Require MFA for all members"
              value={mfaEnforced ? 'on' : 'off'}
              onChange={(v) => handleSetMfa(v === 'on')}
              disabled={mfaSaving}
              options={[
                { value: 'off', label: 'Off' },
                { value: 'on', label: 'On' },
              ]}
            />
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionTitle}>Data retention</div>

        <div style={styles.retentionRow}>
          <div style={styles.retentionExplain}>
            <div style={styles.fieldLabel}>Data retention period (days)</div>
            <div style={styles.domainHint}>
              Sessions and goals are automatically deleted this many days after
              creation. Leave empty to keep data indefinitely.
            </div>
          </div>
          <form onSubmit={handleRetentionDaysSave} style={styles.retentionControl}>
            {retentionDaysSaved && <span style={styles.savedNote}>Saved</span>}
            <div style={{ width: 80, flex: 'none' }}>
              <TextField
                type="number"
                min="1"
                placeholder="disabled"
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
                aria-label="Data retention period in days"
              />
            </div>
            <Button type="submit">Save</Button>
          </form>
        </div>

        <div style={styles.retentionRow}>
          <div style={styles.retentionExplain}>
            <div style={styles.fieldLabel}>Deletion request grace period (days)</div>
            <div style={styles.domainHint}>
              When a user requests account deletion, their data is removed after
              this many days.
            </div>
          </div>
          <form onSubmit={handleGraceDaysSave} style={styles.retentionControl}>
            {graceDaysSaved && <span style={styles.savedNote}>Saved</span>}
            <div style={{ width: 80, flex: 'none' }}>
              <TextField
                type="number"
                min="1"
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                aria-label="Deletion request grace period in days"
              />
            </div>
            <Button type="submit">Save</Button>
          </form>
        </div>

        <div style={{ ...styles.retentionRow, borderBottom: 'none' }}>
          <div style={styles.retentionExplain}>
            <div style={styles.fieldLabel}>Suspension data retention (days)</div>
            <div style={styles.domainHint}>
              How many days after suspension before a deactivated user's data is
              deleted.
            </div>
          </div>
          <form onSubmit={handleSuspensionDaysSave} style={styles.retentionControl}>
            {suspensionDaysSaved && <span style={styles.savedNote}>Saved</span>}
            <div style={{ width: 80, flex: 'none' }}>
              <TextField
                type="number"
                min="1"
                value={suspensionDays}
                onChange={(e) => setSuspensionDays(e.target.value)}
                aria-label="Suspension data retention period in days"
              />
            </div>
            <Button type="submit">Save</Button>
          </form>
        </div>
      </section>

      <ConfirmDialog
        open={mfaEnableConfirm}
        tone="danger"
        title="Enforce MFA for all members?"
        message="All members (including you) will be signed out and required to set up TOTP before they can use the app again. You'll need to enroll TOTP at your next sign-in."
        confirmLabel="Enforce and sign out"
        onConfirm={handleConfirmEnableMfa}
        onCancel={() => setMfaEnableConfirm(false)}
      />

      <ConfirmDialog
        open={!!adminToRemove}
        tone="danger"
        title="Remove administrator"
        message={
          adminToRemove
            ? `Remove ${adminToRemove.name || adminToRemove.email} as an administrator? They will lose admin access immediately.`
            : ''
        }
        confirmLabel="Remove"
        onConfirm={() => {
          if (adminToRemove) removeAdmin(adminToRemove.userId)
          setAdminToRemove(null)
        }}
        onCancel={() => setAdminToRemove(null)}
      />
    </div>
  )
}

const styles = {
  wrap: { maxWidth: 720, margin: '0 auto' },
  title: { fontSize: 20, fontWeight: 600, marginBottom: 24 },
  loading: { fontSize: 13, color: 'var(--text-secondary)', padding: 32 },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginBottom: 16 },
  sectionTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 },
  adminRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid var(--border)' },
  adminName: { fontSize: 13, fontWeight: 500 },
  adminEmail: { fontSize: 11, color: 'var(--text-secondary)' },
  you: { fontSize: 11, color: 'var(--text-secondary)' },
  addForm: { display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' },
  domainHint: { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 },
  fieldLabel: { fontSize: 12, fontWeight: 500, marginBottom: 4 },
  chipList: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12 },
  chipRemove: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: 'var(--text-secondary)', padding: 0 },
  savedNote: { fontSize: 11, color: 'var(--text-secondary)' },
  retentionRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center', padding: '12px 0', borderBottom: '0.5px solid var(--border)' },
  retentionExplain: { minWidth: 0 },
  retentionControl: { display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' },
}
